import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BG =
  "https://static.prod-images.emergentagent.com/jobs/6cd1aa1b-7a17-4cd4-b3df-2ead6d3d2c8f/images/af5a4077a761c1a727bd8924f0c5064e7a69f43ac37899ca663bfc46e0dd30d0.png";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email.trim().toLowerCase(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="relative min-h-screen w-full bg-zinc-950 text-zinc-100 flex items-center justify-center px-4"
      data-testid="login-page"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${BG})` }}
      />
      <div className="absolute inset-0 bg-black/70" />

      <form
        onSubmit={onSubmit}
        className="relative z-10 w-full max-w-sm border border-zinc-800 bg-zinc-950/80 backdrop-blur-sm p-8"
        data-testid="login-form"
      >
        <div className="mb-8">
          <img
            src={require("../assets/purely_it_logo.png")}
            alt="Purely IT Temp Monitor"
            className="h-24 w-auto mx-auto mb-6"
          />
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1 text-center">
            Purely IT Temp Monitor
          </div>
          <h1 className="font-display text-4xl font-black tracking-tight uppercase text-center">
            Sign in
          </h1>
        </div>

        <div className="space-y-5">
          <div>
            <Label htmlFor="email" className="text-xs uppercase tracking-widest text-zinc-400">
              Email
            </Label>
            <Input
              id="email"
              data-testid="login-email-input"
              type="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-2 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs uppercase tracking-widest text-zinc-400">
              Password
            </Label>
            <Input
              id="password"
              data-testid="login-password-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-2 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm"
            />
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="text-sm text-red-400 border-l-2 border-red-500 pl-3"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit-button"
            className="w-full rounded-sm bg-zinc-100 text-zinc-950 hover:bg-white font-bold uppercase tracking-widest text-sm h-11"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </div>

        <div className="mt-8 pt-6 border-t border-zinc-800 text-center text-xs uppercase tracking-widest text-zinc-500">
          <Link
            to="/status"
            data-testid="login-public-status-link"
            className="hover:text-zinc-300 transition-colors"
          >
            View public status →
          </Link>
        </div>
      </form>
    </div>
  );
}
